import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  FileText, 
  Wand2,
  Search,
  BookOpen,
  Copy,
  Download,
  ArrowLeft,
  RefreshCw,
  Lightbulb,
  CheckCircle2,
  BarChart3,
  Repeat,
  Save,
  Trash2,
  Edit,
  FolderOpen,
  Zap,
  ChevronRight,
  Home
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ToolSEOContent from "@/components/ToolSEOContent";
import WhatsAppChat from "@/components/WhatsAppChat";
import RelatedTools from "@/components/RelatedTools";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Download content as text file
const downloadAsTextFile = (content, filename, format = 'md') => {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.${format}`;
  link.click();
  URL.revokeObjectURL(link.href);
  toast.success(`Downloaded ${filename}.${format}`);
};

export default function ContentTools() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTool, setActiveTool] = useState("ai-writer");

  // Get tool from URL params
  useEffect(() => {
    const toolFromUrl = searchParams.get('tool');
    if (toolFromUrl) {
      setActiveTool(toolFromUrl);
    }
  }, [searchParams]);

  // AI Writer State
  const [topic, setTopic] = useState("");
  const [contentType, setContentType] = useState("blog_post");
  const [tone, setTone] = useState("professional");
  const [keywords, setKeywords] = useState("");
  const [length, setLength] = useState("medium");
  const [generatedContent, setGeneratedContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);

  // Topic Research State
  const [researchTopic, setResearchTopic] = useState("");
  const [topicIdeas, setTopicIdeas] = useState(null);
  const [loadingTopics, setLoadingTopics] = useState(false);

  // Content Analyzer State
  const [analyzeUrl, setAnalyzeUrl] = useState("");
  const [targetKeyword, setTargetKeyword] = useState("");
  const [contentAnalysis, setContentAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  // Readability Checker State
  const [readabilityText, setReadabilityText] = useState("");
  const [readabilityResult, setReadabilityResult] = useState(null);
  const [loadingReadability, setLoadingReadability] = useState(false);

  // Content Calendar State
  const [calendarEvents, setCalendarEvents] = useState([
    { id: 1, title: "Blog Post: SEO Tips", date: "2025-02-20", type: "blog", status: "scheduled" },
    { id: 2, title: "Social Media Campaign", date: "2025-02-22", type: "social", status: "draft" },
    { id: 3, title: "Newsletter: Monthly Update", date: "2025-02-25", type: "email", status: "scheduled" }
  ]);
  const [newEvent, setNewEvent] = useState({ title: "", date: "", type: "blog" });

  // Plagiarism Detector State
  const [plagiarismText, setPlagiarismText] = useState("");
  const [plagiarismResult, setPlagiarismResult] = useState(null);
  const [loadingPlagiarism, setLoadingPlagiarism] = useState(false);
  // Grammar Check State (integrated with plagiarism)
  const [grammarResult, setGrammarResult] = useState(null);
  const [loadingGrammar, setLoadingGrammar] = useState(false);
  const [showGrammarCheck, setShowGrammarCheck] = useState(true);

  // Content Templates
  const contentTemplates = [
    { id: "blog-intro", name: "Blog Introduction", category: "Blog", template: "Start with a hook that grabs attention. Introduce the topic and explain why it matters to your readers. Preview what they'll learn in this article." },
    { id: "listicle", name: "Listicle Post", category: "Blog", template: "# [Number] [Topic] That [Benefit]\n\n## 1. [First Item]\n[Description]\n\n## 2. [Second Item]\n[Description]\n\n## Conclusion\n[Wrap up with key takeaways]" },
    { id: "how-to", name: "How-To Guide", category: "Blog", template: "# How to [Achieve Goal]\n\n## What You'll Need\n- [Requirement 1]\n- [Requirement 2]\n\n## Step 1: [Action]\n[Details]\n\n## Step 2: [Action]\n[Details]\n\n## Tips for Success\n[Pro tips]" },
    { id: "product-desc", name: "Product Description", category: "E-commerce", template: "**[Product Name]** - [Tagline]\n\n✨ Key Features:\n• [Feature 1]\n• [Feature 2]\n• [Feature 3]\n\n💡 Perfect for: [Target audience]\n\n🎁 What's included: [Package contents]" },
    { id: "email-newsletter", name: "Email Newsletter", category: "Email", template: "Subject: [Compelling Subject Line]\n\nHi [Name],\n\n[Opening hook - 1-2 sentences]\n\n[Main content - value/update]\n\n[Call to action]\n\nBest,\n[Your name]" },
    { id: "social-caption", name: "Social Media Caption", category: "Social", template: "[Hook - first line is crucial]\n\n[Story or value - 2-3 sentences]\n\n[Call to action]\n\n[Hashtags - 5-10 relevant ones]" },
    { id: "press-release", name: "Press Release", category: "PR", template: "FOR IMMEDIATE RELEASE\n\n**[Headline]**\n*[Subheadline]*\n\n[City, Date] — [Opening paragraph - who, what, when, where, why]\n\n[Body - supporting details]\n\n[Quote from spokesperson]\n\n[Boilerplate about company]\n\nContact: [Name, Email, Phone]" },
    { id: "case-study", name: "Case Study", category: "Business", template: "# [Client Name] Case Study\n\n## The Challenge\n[Describe the problem]\n\n## Our Solution\n[What you did]\n\n## The Results\n• [Metric 1]: [Improvement]\n• [Metric 2]: [Improvement]\n\n## Client Testimonial\n\"[Quote]\" - [Name, Title]" }
  ];

  // Content Scoring State
  const [scoreContent, setScoreContent] = useState("");
  const [contentScoreResult, setContentScoreResult] = useState(null);
  const [loadingScore, setLoadingScore] = useState(false);

  // Headline Analyzer State
  const [headlineText, setHeadlineText] = useState("");
  const [headlineResult, setHeadlineResult] = useState(null);
  const [loadingHeadline, setLoadingHeadline] = useState(false);

  // Content Scoring Handler
  const handleContentScore = () => {
    if (!scoreContent.trim()) {
      toast.error("Please enter content to score");
      return;
    }
    setLoadingScore(true);
    
    setTimeout(() => {
      const text = scoreContent.trim();
      const words = text.split(/\s+/).length;
      const sentences = text.split(/[.!?]+/).filter(s => s.trim()).length;
      const avgWordsPerSentence = words / Math.max(sentences, 1);
      const paragraphs = text.split(/\n\n+/).length;
      const hasHeadings = /#|<h[1-6]>/i.test(text);
      const hasBullets = /[-*•]/.test(text);
      const hasKeywords = words > 300;
      
      // Calculate scores
      const readabilityScore = Math.max(0, Math.min(100, 100 - (avgWordsPerSentence - 15) * 3));
      const structureScore = (hasHeadings ? 30 : 0) + (hasBullets ? 20 : 0) + (paragraphs >= 3 ? 25 : paragraphs * 8) + (words >= 300 ? 25 : words / 12);
      const seoScore = (words >= 300 ? 40 : words / 7.5) + (hasHeadings ? 30 : 0) + (paragraphs >= 3 ? 30 : 10);
      const overallScore = Math.round((readabilityScore * 0.3 + structureScore * 0.35 + seoScore * 0.35));
      
      setContentScoreResult({
        overall: overallScore,
        readability: Math.round(readabilityScore),
        structure: Math.round(Math.min(100, structureScore)),
        seo: Math.round(Math.min(100, seoScore)),
        stats: { words, sentences, paragraphs, avgWordsPerSentence: avgWordsPerSentence.toFixed(1) },
        suggestions: [
          words < 300 ? "Add more content (aim for 300+ words)" : null,
          !hasHeadings ? "Add headings to improve structure" : null,
          !hasBullets ? "Use bullet points for better readability" : null,
          avgWordsPerSentence > 20 ? "Shorten sentences for easier reading" : null,
          paragraphs < 3 ? "Break content into more paragraphs" : null
        ].filter(Boolean)
      });
      setLoadingScore(false);
      toast.success("Content scored!");
    }, 500);
  };

  // Headline Analyzer Handler  
  const handleHeadlineAnalysis = () => {
    if (!headlineText.trim()) {
      toast.error("Please enter a headline");
      return;
    }
    setLoadingHeadline(true);
    
    setTimeout(() => {
      const headline = headlineText.trim();
      const wordCount = headline.split(/\s+/).length;
      const charCount = headline.length;
      const hasNumbers = /\d/.test(headline);
      const hasQuestion = headline.includes('?');
      const hasPowerWords = /best|top|ultimate|secret|hack|proven|amazing|how|why|what|guide|tips|ways|mistakes|free/i.test(headline);
      const hasEmotional = /love|hate|fear|amazing|incredible|shocking|surprising|unbelievable/i.test(headline);
      const hasColon = headline.includes(':');
      const hasBrackets = /\[|\(/.test(headline);
      
      let score = 40;
      if (wordCount >= 6 && wordCount <= 12) score += 15;
      if (charCount >= 40 && charCount <= 70) score += 10;
      if (hasNumbers) score += 12;
      if (hasQuestion) score += 8;
      if (hasPowerWords) score += 10;
      if (hasEmotional) score += 8;
      if (hasColon) score += 5;
      if (hasBrackets) score += 5;
      
      const finalScore = Math.min(100, Math.round(score));
      
      setHeadlineResult({
        score: finalScore,
        grade: finalScore >= 80 ? 'A' : finalScore >= 60 ? 'B' : finalScore >= 40 ? 'C' : 'D',
        wordCount, charCount,
        factors: {
          hasNumbers, hasQuestion, hasPowerWords, hasEmotional, hasColon, hasBrackets,
          lengthOptimal: wordCount >= 6 && wordCount <= 12
        },
        suggestions: [
          !hasNumbers ? "Add a number (e.g., '7 Tips', '2024')" : null,
          !hasPowerWords ? "Include power words (Best, Ultimate, Guide)" : null,
          wordCount < 6 ? "Add more words for context" : null,
          wordCount > 12 ? "Shorten for better readability" : null,
          !hasQuestion && !hasEmotional ? "Try making it emotional or asking a question" : null
        ].filter(Boolean)
      });
      setLoadingHeadline(false);
      toast.success("Headline analyzed!");
    }, 400);
  };

  // Content Rewriter State
  const [rewriteText, setRewriteText] = useState("");
  const [rewriteStyle, setRewriteStyle] = useState("professional");
  const [preserveLength, setPreserveLength] = useState(true);
  const [rewriteResult, setRewriteResult] = useState(null);
  const [loadingRewrite, setLoadingRewrite] = useState(false);

  // Drafts State
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  // Fetch drafts on mount
  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const response = await axios.get(`${API}/content/drafts/list`, { withCredentials: true, headers: { 'X-Skip-Auth-Error': 'true' } });
      setDrafts(response.data.drafts || []);
    } catch (error) {
      console.log("Could not fetch drafts");
    } finally {
      setLoadingDrafts(false);
    }
  };

  const saveToDrafts = async () => {
    if (!generatedContent) {
      toast.error("No content to save");
      return;
    }

    setSavingDraft(true);
    try {
      const response = await axios.post(`${API}/content/drafts/save`, {
        title: generatedContent.topic || topic,
        content: generatedContent.content,
        content_type: contentType,
        tone: tone,
        keywords: keywords.split(',').map(k => k.trim()).filter(k => k),
        word_count: generatedContent.word_count
      }, { withCredentials: true });

      toast.success(response.data.message || "Draft saved!");
      fetchDrafts(); // Refresh drafts list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const deleteDraft = async (draftId) => {
    try {
      await axios.delete(`${API}/content/drafts/${draftId}`, { withCredentials: true });
      toast.success("Draft deleted");
      setDrafts(drafts.filter(d => d.draft_id !== draftId));
    } catch (error) {
      toast.error("Failed to delete draft");
    }
  };

  const loadDraft = (draft) => {
    setTopic(draft.title);
    setContentType(draft.content_type || "blog_post");
    setTone(draft.tone || "professional");
    setKeywords(draft.keywords?.join(', ') || "");
    setGeneratedContent({
      topic: draft.title,
      content: draft.content,
      word_count: draft.word_count
    });
    setActiveTool("ai-writer");
    toast.success("Draft loaded!");
  };

  const contentTools = [
    {
      id: "ai-writer",
      name: "AI Content Writer",
      icon: <Wand2 className="w-5 h-5" />,
      description: "Generate SEO-optimized content with AI"
    },
    {
      id: "my-drafts",
      name: "My Drafts",
      icon: <FolderOpen className="w-5 h-5" />,
      description: "View and manage saved drafts",
      badge: drafts.length > 0 ? drafts.length : null
    },
    {
      id: "topic-research",
      name: "Topic Research",
      icon: <Lightbulb className="w-5 h-5" />,
      description: "Discover trending topic ideas"
    },
    {
      id: "content-analyzer",
      name: "Content Analyzer",
      icon: <BarChart3 className="w-5 h-5" />,
      description: "Analyze content for SEO & readability"
    },
    {
      id: "readability",
      name: "Readability Checker",
      icon: <BookOpen className="w-5 h-5" />,
      description: "Check content readability score"
    },
    {
      id: "plagiarism",
      name: "Plagiarism & Grammar",
      icon: <Search className="w-5 h-5" />,
      description: "Check plagiarism + grammar"
    },
    {
      id: "content-score",
      name: "Content Scoring",
      icon: <BarChart3 className="w-5 h-5" />,
      description: "SEO + Readability combined score",
      badge: "NEW"
    },
    {
      id: "headline-analyzer",
      name: "Headline Analyzer",
      icon: <Lightbulb className="w-5 h-5" />,
      description: "Score your headlines for CTR",
      badge: "NEW"
    },
    {
      id: "templates",
      name: "Content Templates",
      icon: <FileText className="w-5 h-5" />,
      description: "Ready-to-use content templates",
      badge: "NEW"
    },
    {
      id: "rewriter",
      name: "AI Content Rewriter",
      icon: <Repeat className="w-5 h-5" />,
      description: "Paraphrase text with AI"
    },
    {
      id: "calendar",
      name: "Content Calendar",
      icon: <FileText className="w-5 h-5" />,
      description: "Plan your content schedule"
    }
  ];

  const handleGenerateContent = async () => {
    if (!topic.trim()) {
      toast.error("Please enter a topic");
      return;
    }

    setLoadingContent(true);
    try {
      const keywordsList = keywords.split(',').map(k => k.trim()).filter(k => k);
      
      const response = await axios.post(`${API}/content/generate`, {
        topic: topic.trim(),
        content_type: contentType,
        tone: tone,
        keywords: keywordsList.length > 0 ? keywordsList : null,
        length: length
      });
      
      setGeneratedContent(response.data);
      toast.success("Content generated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate content");
    } finally {
      setLoadingContent(false);
    }
  };

  const handleTopicResearch = async () => {
    if (!researchTopic.trim()) {
      toast.error("Please enter a topic");
      return;
    }

    setLoadingTopics(true);
    try {
      const response = await axios.post(`${API}/content/topics`, {
        topic: researchTopic.trim(),
        num_ideas: 10
      });
      
      setTopicIdeas(response.data);
      toast.success("Topic ideas generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate topics");
    } finally {
      setLoadingTopics(false);
    }
  };

  const handleContentAnalysis = async () => {
    if (!analyzeUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    setLoadingAnalysis(true);
    try {
      const response = await axios.post(`${API}/content/analyze`, {
        url: analyzeUrl.trim(),
        target_keyword: targetKeyword.trim() || null
      });
      
      setContentAnalysis(response.data);
      toast.success("Content analysis complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze content");
    } finally {
      setLoadingAnalysis(false);
    }
  };

  // Plagiarism Checker Function
  const handlePlagiarismCheck = async () => {
    if (!plagiarismText.trim()) {
      toast.error("Please enter some text to check");
      return;
    }

    if (plagiarismText.trim().length < 100) {
      toast.error("Please enter at least 100 characters for accurate plagiarism check");
      return;
    }

    setLoadingPlagiarism(true);
    try {
      const response = await axios.post(`${API}/plagiarism/check-text`, {
        content: plagiarismText.trim(),
        check_type: "internet"
      });
      
      setPlagiarismResult(response.data);
      toast.success("Plagiarism check complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check plagiarism");
    } finally {
      setLoadingPlagiarism(false);
    }
  };

  // Content Rewriter Function
  const handleContentRewrite = async () => {
    if (!rewriteText.trim()) {
      toast.error("Please enter some text to rewrite");
      return;
    }

    if (rewriteText.trim().length < 50) {
      toast.error("Please enter at least 50 characters for rewriting");
      return;
    }

    setLoadingRewrite(true);
    try {
      const response = await axios.post(`${API}/content/rewrite`, {
        content: rewriteText.trim(),
        style: rewriteStyle,
        preserve_length: preserveLength
      });
      
      setRewriteResult(response.data);
      toast.success("Content rewritten successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rewrite content");
    } finally {
      setLoadingRewrite(false);
    }
  };

  // Quick Rewrite from Plagiarism Result
  const handleQuickRewrite = () => {
    setRewriteText(plagiarismText);
    setActiveTool("rewriter");
  };

  // Grammar Check Function
  const handleGrammarCheck = async () => {
    if (!plagiarismText.trim()) {
      toast.error("Please enter text to check");
      return;
    }

    setLoadingGrammar(true);
    try {
      const text = plagiarismText.trim();
      const issues = [];
      
      const doubleSpaces = (text.match(/  +/g) || []).length;
      if (doubleSpaces > 0) issues.push({ type: 'spacing', message: `${doubleSpaces} double spaces found`, severity: 'low' });
      
      const missingCapitals = (text.match(/[.!?]\s+[a-z]/g) || []).length;
      if (missingCapitals > 0) issues.push({ type: 'capitalization', message: `${missingCapitals} sentences may need capitalization`, severity: 'medium' });
      
      const repeatedWords = text.match(/\b(\w+)\s+\1\b/gi) || [];
      if (repeatedWords.length > 0) issues.push({ type: 'repetition', message: `${repeatedWords.length} repeated words found`, severity: 'low' });
      
      const commonMistakes = [
        { pattern: /\btheir\s+is\b/gi, fix: 'there is' },
        { pattern: /\bits\s+a\b/gi, fix: "it's a" },
        { pattern: /\byour\s+welcome\b/gi, fix: "you're welcome" },
        { pattern: /\bcould\s+of\b/gi, fix: 'could have' },
        { pattern: /\bshould\s+of\b/gi, fix: 'should have' },
        { pattern: /\balot\b/gi, fix: 'a lot' }
      ];
      
      commonMistakes.forEach(({ pattern, fix }) => {
        const matches = text.match(pattern);
        if (matches) issues.push({ type: 'grammar', message: `Consider "${fix}" instead`, severity: 'high' });
      });
      
      const score = Math.max(0, 100 - (issues.filter(i => i.severity === 'high').length * 15) - (issues.filter(i => i.severity === 'medium').length * 5) - (issues.filter(i => i.severity === 'low').length * 2));
      
      setGrammarResult({
        score: Math.round(score),
        issues,
        wordCount: text.split(/\s+/).length,
        suggestions: issues.length === 0 ? ['Your text looks grammatically correct!'] : issues.map(i => i.message)
      });
      
      toast.success("Grammar check complete!");
    } catch (error) {
      toast.error("Grammar check failed");
    } finally {
      setLoadingGrammar(false);
    }
  };

  // Load Template
  const loadTemplate = (template) => {
    setTopic(template.name);
    setGeneratedContent({
      topic: template.name,
      content: template.template,
      word_count: template.template.split(/\s+/).length
    });
    setActiveTool("ai-writer");
    toast.success(`Template "${template.name}" loaded!`);
  };

  // Readability Checker Function
  const handleReadabilityCheck = () => {
    if (!readabilityText.trim()) {
      toast.error("Please enter some text to analyze");
      return;
    }

    setLoadingReadability(true);
    
    // Calculate readability metrics
    setTimeout(() => {
      const text = readabilityText.trim();
      const words = text.split(/\s+/).filter(w => w.length > 0);
      const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
      const syllables = words.reduce((count, word) => {
        return count + countSyllables(word);
      }, 0);
      
      const avgWordsPerSentence = words.length / Math.max(sentences.length, 1);
      const avgSyllablesPerWord = syllables / Math.max(words.length, 1);
      
      // Flesch Reading Ease Score
      const fleschScore = Math.round(206.835 - (1.015 * avgWordsPerSentence) - (84.6 * avgSyllablesPerWord));
      
      // Reading level
      let readingLevel = "College Graduate";
      let difficulty = "Very Hard";
      if (fleschScore >= 90) { readingLevel = "5th Grade"; difficulty = "Very Easy"; }
      else if (fleschScore >= 80) { readingLevel = "6th Grade"; difficulty = "Easy"; }
      else if (fleschScore >= 70) { readingLevel = "7th Grade"; difficulty = "Fairly Easy"; }
      else if (fleschScore >= 60) { readingLevel = "8th-9th Grade"; difficulty = "Standard"; }
      else if (fleschScore >= 50) { readingLevel = "10th-12th Grade"; difficulty = "Fairly Hard"; }
      else if (fleschScore >= 30) { readingLevel = "College"; difficulty = "Hard"; }
      
      setReadabilityResult({
        fleschScore: Math.max(0, Math.min(100, fleschScore)),
        readingLevel,
        difficulty,
        wordCount: words.length,
        sentenceCount: sentences.length,
        avgWordsPerSentence: avgWordsPerSentence.toFixed(1),
        avgSyllablesPerWord: avgSyllablesPerWord.toFixed(2),
        suggestions: [
          avgWordsPerSentence > 20 ? "Consider shorter sentences for better readability" : null,
          avgSyllablesPerWord > 1.5 ? "Try using simpler words" : null,
          words.length < 300 ? "Content might be too short for SEO" : null,
        ].filter(Boolean)
      });
      
      setLoadingReadability(false);
      toast.success("Readability analysis complete!");
    }, 1000);
  };

  // Helper function to count syllables
  const countSyllables = (word) => {
    word = word.toLowerCase().replace(/[^a-z]/g, '');
    if (word.length <= 3) return 1;
    word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '');
    word = word.replace(/^y/, '');
    const matches = word.match(/[aeiouy]{1,2}/g);
    return matches ? matches.length : 1;
  };

  // Content Calendar Functions
  const addCalendarEvent = () => {
    if (!newEvent.title || !newEvent.date) {
      toast.error("Please fill in all fields");
      return;
    }
    
    const event = {
      id: Date.now(),
      ...newEvent,
      status: "draft"
    };
    
    setCalendarEvents([...calendarEvents, event]);
    setNewEvent({ title: "", date: "", type: "blog" });
    toast.success("Content scheduled!");
  };

  const deleteCalendarEvent = (id) => {
    setCalendarEvents(calendarEvents.filter(e => e.id !== id));
    toast.success("Event deleted");
  };

  const toggleEventStatus = (id) => {
    setCalendarEvents(calendarEvents.map(e => 
      e.id === id ? { ...e, status: e.status === "draft" ? "scheduled" : "draft" } : e
    ));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getDifficultyColor = (difficulty) => {
    switch(difficulty?.toLowerCase()) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-sm mb-4" aria-label="Breadcrumb">
          <Link to="/" className="flex items-center gap-1 text-gray-500 hover:text-purple-600 transition-colors">
            <Home className="w-4 h-4" />
            <span>Home</span>
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <Link to="/tools" className="text-gray-500 hover:text-purple-600 transition-colors">
            Tools
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className="text-gray-900 font-medium">Content Tools</span>
        </nav>

        {/* Page Title — Premium */}
        <div className="mb-10">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#111111] rounded-2xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs tracking-[0.18em] font-semibold text-[#A855F7] uppercase mb-1">Content Suite</p>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#111111]" data-testid="page-title">Content Tools</h1>
              <p className="text-slate-600 mt-1.5">Create, optimize, and manage your content with AI-powered tools</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-4 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">Content Tools</h3>
              <div className="space-y-2">
                {contentTools.map((tool) => (
                  <button
                    key={tool.id}
                    onClick={() => setActiveTool(tool.id)}
                    data-testid={`tool-${tool.id}`}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                      activeTool === tool.id
                        ? 'bg-purple-100 text-purple-700 font-medium'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {tool.icon}
                        <span className="text-sm">{tool.name}</span>
                      </div>
                      {tool.badge && (
                        <Badge className="bg-purple-600 text-white text-xs">{tool.badge}</Badge>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* AI Content Writer */}
            {activeTool === "ai-writer" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-lg flex items-center justify-center">
                    <Wand2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">AI Content Writer</h2>
                    <p className="text-gray-600">Generate SEO-optimized content with GPT-4o</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Topic / Title</label>
                    <Input
                      placeholder="e.g., How to improve website SEO in 2024"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      data-testid="content-topic-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Content Type</label>
                      <Select value={contentType} onValueChange={setContentType}>
                        <SelectTrigger data-testid="content-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="blog_post">Blog Post</SelectItem>
                          <SelectItem value="product_description">Product Description</SelectItem>
                          <SelectItem value="social_post">Social Media Posts</SelectItem>
                          <SelectItem value="email">Marketing Email</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Tone</label>
                      <Select value={tone} onValueChange={setTone}>
                        <SelectTrigger data-testid="tone-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="casual">Casual</SelectItem>
                          <SelectItem value="persuasive">Persuasive</SelectItem>
                          <SelectItem value="informative">Informative</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Word Count</label>
                      <Select value={length} onValueChange={setLength}>
                        <SelectTrigger data-testid="length-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="short">Short (~300 words)</SelectItem>
                          <SelectItem value="500">500 words</SelectItem>
                          <SelectItem value="medium">Medium (~800 words)</SelectItem>
                          <SelectItem value="1000">1,000 words</SelectItem>
                          <SelectItem value="long">Long (~1,500 words)</SelectItem>
                          <SelectItem value="2000">2,000 words</SelectItem>
                          <SelectItem value="3000">3,000 words (Long-form)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">SEO Keywords</label>
                      <Input
                        placeholder="keyword1, keyword2"
                        value={keywords}
                        onChange={(e) => setKeywords(e.target.value)}
                        data-testid="keywords-input"
                      />
                    </div>
                  </div>

                  <Button 
                    onClick={handleGenerateContent}
                    disabled={loadingContent}
                    className="w-full bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700"
                    data-testid="generate-content-btn"
                  >
                    {loadingContent ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating Content...</>
                    ) : (
                      <><Wand2 className="w-4 h-4 mr-2" /> Generate Content</>
                    )}
                  </Button>
                </div>

                {generatedContent && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="font-medium">Content Generated</span>
                        <Badge variant="outline">{generatedContent.word_count} words</Badge>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => copyToClipboard(generatedContent.content)}
                          data-testid="copy-content-btn"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          data-testid="download-content-btn"
                          onClick={() => downloadAsTextFile(generatedContent.content, generatedContent.topic.replace(/\s+/g, '_').toLowerCase())}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                        <Button 
                          size="sm"
                          onClick={saveToDrafts}
                          disabled={savingDraft}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          data-testid="save-draft-btn"
                        >
                          {savingDraft ? (
                            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                          ) : (
                            <><Save className="w-4 h-4 mr-2" /> Save to Drafts</>
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="p-6 bg-white border rounded-lg prose prose-violet max-w-none prose-headings:font-bold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-gray-700 prose-p:leading-relaxed prose-li:text-gray-700 prose-strong:text-gray-900">
                      <ReactMarkdown>{generatedContent.content}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Topic Research */}
            {activeTool === "topic-research" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Lightbulb className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Topic Research</h2>
                    <p className="text-gray-600">Discover trending content ideas</p>
                  </div>
                </div>

                <div className="flex gap-4 mb-6">
                  <Input
                    placeholder="Enter a seed topic (e.g., digital marketing)"
                    value={researchTopic}
                    onChange={(e) => setResearchTopic(e.target.value)}
                    className="flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && handleTopicResearch()}
                    data-testid="research-topic-input"
                  />
                  <Button 
                    onClick={handleTopicResearch}
                    disabled={loadingTopics}
                    className="bg-yellow-500 hover:bg-yellow-600"
                    data-testid="research-topics-btn"
                  >
                    {loadingTopics ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Researching...</>
                    ) : (
                      <><Lightbulb className="w-4 h-4 mr-2" /> Generate Ideas</>
                    )}
                  </Button>
                </div>

                {topicIdeas && topicIdeas.topics && topicIdeas.topics.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="font-semibold">
                      Found {topicIdeas.total_ideas} Topic Ideas
                    </h3>
                    <div className="space-y-3">
                      {topicIdeas.topics.map((idea, idx) => (
                        <div key={idx} className="p-4 bg-gray-50 rounded-lg border hover:border-yellow-300 transition-colors">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{idea.title}</h4>
                              <div className="flex flex-wrap gap-2 mt-2">
                                <Badge variant="outline">{idea.keyword}</Badge>
                                <Badge className="bg-blue-100 text-blue-800">{idea.content_type}</Badge>
                                <Badge className="bg-purple-100 text-purple-800">{idea.search_intent}</Badge>
                                <Badge className={getDifficultyColor(idea.difficulty)}>{idea.difficulty}</Badge>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  navigator.clipboard.writeText(idea.title);
                                  toast.success("Topic copied to clipboard!");
                                }}
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Copy Topic
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  navigator.clipboard.writeText(idea.keyword);
                                  toast.success("Keyword copied!");
                                }}
                              >
                                Copy Keyword
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {topicIdeas && topicIdeas.raw_response && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium mb-2">Generated Ideas:</h4>
                    <pre className="whitespace-pre-wrap text-sm">{topicIdeas.raw_response}</pre>
                  </div>
                )}
              </Card>
            )}

            {/* Content Analyzer */}
            {activeTool === "content-analyzer" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Content Analyzer</h2>
                    <p className="text-gray-600">Analyze content for SEO and readability</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <Input
                    placeholder="Enter URL to analyze"
                    value={analyzeUrl}
                    onChange={(e) => setAnalyzeUrl(e.target.value)}
                    data-testid="analyze-url-input"
                  />
                  <Input
                    placeholder="Target keyword (optional)"
                    value={targetKeyword}
                    onChange={(e) => setTargetKeyword(e.target.value)}
                    data-testid="target-keyword-input"
                  />
                  <Button 
                    onClick={handleContentAnalysis}
                    disabled={loadingAnalysis}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="analyze-content-btn"
                  >
                    {loadingAnalysis ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                    ) : "Analyze Content"}
                  </Button>
                </div>

                {contentAnalysis && (
                  <div className="space-y-6">
                    {/* Readability */}
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h3 className="font-semibold mb-3">Readability Score</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Flesch Reading Ease</p>
                          <p className="text-3xl font-bold text-blue-600">
                            {contentAnalysis.content_analysis.readability.flesch_reading_ease}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Grade Level</p>
                          <p className="text-lg font-medium">
                            {contentAnalysis.content_analysis.readability.grade_level}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Word Count</p>
                          <p className="text-3xl font-bold">
                            {contentAnalysis.content_analysis.readability.word_count}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Avg Sentence Length</p>
                          <p className="text-lg font-medium">
                            {contentAnalysis.content_analysis.readability.avg_sentence_length} words
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Keyword Density */}
                    {contentAnalysis.content_analysis.keyword_density && (
                      <div className="p-4 bg-green-50 rounded-lg">
                        <h3 className="font-semibold mb-3">Keyword Density</h3>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600">
                              Target: {contentAnalysis.content_analysis.keyword_density.keyword}
                            </p>
                            <p className="text-3xl font-bold text-green-600">
                              {contentAnalysis.content_analysis.keyword_density.density}%
                            </p>
                            <p className="text-sm text-gray-600">
                              ({contentAnalysis.content_analysis.keyword_density.count} mentions)
                            </p>
                          </div>
                          <Badge className={
                            contentAnalysis.content_analysis.keyword_density.status === 'optimal' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-orange-100 text-orange-800'
                          }>
                            {contentAnalysis.content_analysis.keyword_density.recommendation}
                          </Badge>
                        </div>
                      </div>
                    )}

                    {/* Suggestions */}
                    {contentAnalysis.content_analysis.suggestions && 
                     contentAnalysis.content_analysis.suggestions.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3">Suggestions</h3>
                        <div className="space-y-2">
                          {contentAnalysis.content_analysis.suggestions.map((sug, idx) => (
                            <div key={idx} className="p-3 bg-orange-50 rounded border-l-4 border-orange-400">
                              <p className="font-medium text-sm">{sug.message}</p>
                              <p className="text-sm text-gray-600 mt-1">{sug.recommendation}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Top Keywords */}
                    {contentAnalysis.content_analysis.keywords && (
                      <div>
                        <h3 className="font-semibold mb-3">Top Keywords Found</h3>
                        <div className="flex flex-wrap gap-2">
                          {contentAnalysis.content_analysis.keywords.slice(0, 15).map((kw, idx) => (
                            <Badge key={idx} variant="outline">
                              {kw.keyword} ({kw.count})
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* Placeholder for other tools */}
            {/* Readability Checker */}
            {activeTool === "readability" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Readability Checker</h2>
                    <p className="text-gray-600">Analyze your content's readability score</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <Textarea
                    placeholder="Paste your content here to check readability..."
                    value={readabilityText}
                    onChange={(e) => setReadabilityText(e.target.value)}
                    rows={8}
                    className="w-full"
                    data-testid="readability-input"
                  />
                  
                  <Button 
                    onClick={handleReadabilityCheck}
                    disabled={loadingReadability}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="check-readability-btn"
                  >
                    {loadingReadability ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                    ) : (
                      <><BookOpen className="w-4 h-4 mr-2" /> Check Readability</>
                    )}
                  </Button>
                </div>

                {readabilityResult && (
                  <div className="mt-6 space-y-4">
                    {/* Score Display */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Flesch Score</p>
                        <p className="text-3xl font-bold text-blue-600">{readabilityResult.fleschScore}</p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Reading Level</p>
                        <p className="text-lg font-bold text-green-600">{readabilityResult.readingLevel}</p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Word Count</p>
                        <p className="text-3xl font-bold text-purple-600">{readabilityResult.wordCount}</p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Sentences</p>
                        <p className="text-3xl font-bold text-orange-600">{readabilityResult.sentenceCount}</p>
                      </div>
                    </div>

                    {/* Detailed Stats */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold mb-3">Detailed Analysis</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Avg. Words/Sentence:</span>
                          <span className="font-medium">{readabilityResult.avgWordsPerSentence}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Avg. Syllables/Word:</span>
                          <span className="font-medium">{readabilityResult.avgSyllablesPerWord}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Difficulty:</span>
                          <Badge className={getDifficultyColor(readabilityResult.difficulty)}>
                            {readabilityResult.difficulty}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Suggestions */}
                    {readabilityResult.suggestions.length > 0 && (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <h4 className="font-semibold text-amber-800 mb-2">Suggestions</h4>
                        <ul className="space-y-1">
                          {readabilityResult.suggestions.map((suggestion, idx) => (
                            <li key={idx} className="text-sm text-amber-700 flex items-start gap-2">
                              <span>•</span>
                              <span>{suggestion}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* Content Calendar */}
            {activeTool === "calendar" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-500 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Content Calendar</h2>
                    <p className="text-gray-600">Plan and schedule your content</p>
                  </div>
                </div>

                {/* Add New Event */}
                <div className="p-4 bg-gray-50 rounded-lg mb-6">
                  <h4 className="font-semibold mb-3">Schedule New Content</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <Input
                      placeholder="Content title"
                      value={newEvent.title}
                      onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                      className="md:col-span-2"
                      data-testid="calendar-title-input"
                    />
                    <Input
                      type="date"
                      value={newEvent.date}
                      onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                      data-testid="calendar-date-input"
                    />
                    <Select value={newEvent.type} onValueChange={(v) => setNewEvent({ ...newEvent, type: v })}>
                      <SelectTrigger data-testid="calendar-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="blog">Blog Post</SelectItem>
                        <SelectItem value="social">Social Media</SelectItem>
                        <SelectItem value="email">Email/Newsletter</SelectItem>
                        <SelectItem value="video">Video Content</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={addCalendarEvent}
                    className="mt-3 bg-violet-600 hover:bg-violet-700"
                    data-testid="add-event-btn"
                  >
                    Add to Calendar
                  </Button>
                </div>

                {/* Calendar Events */}
                <div className="space-y-3">
                  <h4 className="font-semibold">Scheduled Content</h4>
                  {calendarEvents.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No content scheduled yet</p>
                  ) : (
                    calendarEvents
                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                      .map((event) => (
                        <div 
                          key={event.id}
                          className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-3 h-3 rounded-full ${
                              event.type === "blog" ? "bg-blue-500" :
                              event.type === "social" ? "bg-pink-500" :
                              event.type === "email" ? "bg-green-500" : "bg-orange-500"
                            }`} />
                            <div>
                              <p className="font-medium">{event.title}</p>
                              <p className="text-sm text-gray-500">
                                {new Date(event.date).toLocaleDateString('en-IN', { 
                                  weekday: 'short', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                                <span className="mx-2">•</span>
                                <Badge variant="outline" className="text-xs">
                                  {event.type}
                                </Badge>
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge 
                              className={event.status === "scheduled" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}
                              onClick={() => toggleEventStatus(event.id)}
                              style={{ cursor: "pointer" }}
                            >
                              {event.status}
                            </Badge>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => deleteCalendarEvent(event.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              ×
                            </Button>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </Card>
            )}

            {/* Plagiarism Detector */}
            {activeTool === "plagiarism" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                    <Search className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Plagiarism Detector</h2>
                    <p className="text-gray-600">Check content for duplicate text using Copyscape</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Paste your content to check</label>
                    <Textarea
                      placeholder="Paste your article, blog post, or any text content here to check for plagiarism (minimum 100 characters)..."
                      rows={8}
                      className="resize-none"
                      value={plagiarismText}
                      onChange={(e) => setPlagiarismText(e.target.value)}
                      data-testid="plagiarism-input"
                    />
                  </div>

                  <div className="flex items-center gap-4">
                    <Button 
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      onClick={handlePlagiarismCheck}
                      disabled={loadingPlagiarism}
                      data-testid="check-plagiarism-btn"
                    >
                      {loadingPlagiarism ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Checking...</>
                      ) : (
                        <><Search className="w-4 h-4 mr-2" /> Check Plagiarism</>
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      className="flex-1"
                      onClick={handleGrammarCheck}
                      disabled={loadingGrammar}
                      data-testid="check-grammar-btn"
                    >
                      {loadingGrammar ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Checking...</>
                      ) : (
                        <><CheckCircle2 className="w-4 h-4 mr-2" /> Check Grammar</>
                      )}
                    </Button>
                  </div>
                  <span className="text-sm text-gray-500 block text-right">{plagiarismText.length} / 25,000 characters</span>

                  {/* Grammar Results */}
                  {grammarResult && (
                    <div className={`p-4 rounded-lg border ${grammarResult.score >= 80 ? 'bg-green-50 border-green-200' : grammarResult.score >= 60 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold flex items-center gap-2">
                          <CheckCircle2 className={`w-5 h-5 ${grammarResult.score >= 80 ? 'text-green-600' : grammarResult.score >= 60 ? 'text-yellow-600' : 'text-red-600'}`} />
                          Grammar Score: {grammarResult.score}/100
                        </h4>
                        <Badge className={grammarResult.score >= 80 ? 'bg-green-100 text-green-700' : grammarResult.score >= 60 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>
                          {grammarResult.issues.length} issues found
                        </Badge>
                      </div>
                      {grammarResult.suggestions.length > 0 && (
                        <ul className="space-y-1 text-sm">
                          {grammarResult.suggestions.map((s, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-gray-400">•</span>
                              <span>{s}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* Results */}
                  {plagiarismResult && (
                    <>
                      <div className={`p-6 rounded-lg border ${
                        plagiarismResult.status === 'excellent' ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' :
                        plagiarismResult.status === 'good' ? 'bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200' :
                        plagiarismResult.status === 'warning' ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200' :
                        'bg-gradient-to-br from-red-50 to-pink-50 border-red-200'
                      }`}>
                        <div className="text-center mb-6">
                          <div className={`inline-flex items-center justify-center w-24 h-24 bg-white rounded-full shadow-lg mb-4`}>
                            <span className={`text-4xl font-bold ${
                              plagiarismResult.status === 'excellent' ? 'text-green-600' :
                              plagiarismResult.status === 'good' ? 'text-blue-600' :
                              plagiarismResult.status === 'warning' ? 'text-yellow-600' : 'text-red-600'
                            }`}>{plagiarismResult.originality_score}%</span>
                          </div>
                          <p className={`text-lg font-semibold ${
                            plagiarismResult.status === 'excellent' ? 'text-green-800' :
                            plagiarismResult.status === 'good' ? 'text-blue-800' :
                            plagiarismResult.status === 'warning' ? 'text-yellow-800' : 'text-red-800'
                          }`}>
                            {plagiarismResult.status === 'excellent' ? 'Highly Original Content' :
                             plagiarismResult.status === 'good' ? 'Good Original Content' :
                             plagiarismResult.status === 'warning' ? 'Content Has Similarities' : 'Potential Plagiarism Detected'}
                          </p>
                          <p className="text-sm text-gray-600">{plagiarismResult.status_message}</p>
                        </div>

                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div className="p-3 bg-white rounded-lg">
                            <p className="text-2xl font-bold text-green-600">{plagiarismResult.originality_score}%</p>
                            <p className="text-xs text-gray-500">Original</p>
                          </div>
                          <div className="p-3 bg-white rounded-lg">
                            <p className="text-2xl font-bold text-red-600">{plagiarismResult.duplicate_percentage}%</p>
                            <p className="text-xs text-gray-500">Duplicate</p>
                          </div>
                          <div className="p-3 bg-white rounded-lg">
                            <p className="text-2xl font-bold text-blue-600">{plagiarismResult.word_count}</p>
                            <p className="text-xs text-gray-500">Words</p>
                          </div>
                        </div>
                      </div>

                      {/* Quick Rewrite Button */}
                      {(plagiarismResult.status === 'warning' || plagiarismResult.status === 'danger') && (
                        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-purple-800">Need to make it unique?</h4>
                              <p className="text-sm text-purple-600">Use AI to rewrite your content and make it original</p>
                            </div>
                            <Button 
                              onClick={handleQuickRewrite}
                              className="bg-purple-600 hover:bg-purple-700"
                              data-testid="quick-rewrite-btn"
                            >
                              <Repeat className="w-4 h-4 mr-2" />
                              Rewrite Content
                            </Button>
                          </div>
                        </div>
                      )}

                      {plagiarismResult.sources && plagiarismResult.sources.length > 0 && (
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-medium mb-3">Sources Found ({plagiarismResult.matches_found})</h4>
                          <div className="space-y-2">
                            {plagiarismResult.sources.map((source, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-white rounded border">
                                <div className="flex-1 min-w-0">
                                  <a 
                                    href={source.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-600 hover:underline truncate block"
                                  >
                                    {source.url}
                                  </a>
                                  <p className="text-xs text-gray-500 truncate">{source.snippet}</p>
                                </div>
                                <Badge variant="outline" className="ml-2">{source.percent_matched}%</Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {plagiarismResult.sources && plagiarismResult.sources.length === 0 && (
                        <div className="p-4 bg-green-50 rounded-lg text-center">
                          <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                          <p className="text-green-800 font-medium">No duplicate sources found!</p>
                          <p className="text-sm text-green-600">Your content appears to be original.</p>
                        </div>
                      )}
                    </>
                  )}

                  {!plagiarismResult && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-800 mb-2">How it works</h4>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Paste your content above (minimum 100 characters)</li>
                        <li>• Our system checks against billions of web pages using Copyscape</li>
                        <li>• Get instant originality score and duplicate sources</li>
                        <li>• Each check uses API credits</li>
                      </ul>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Content Scoring */}
            {activeTool === "content-score" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Content Scoring</h2>
                    <p className="text-gray-600">Get SEO + Readability combined score</p>
                  </div>
                  <Badge className="ml-auto bg-gradient-to-r from-indigo-500 to-purple-500 text-white">NEW</Badge>
                </div>

                <Textarea
                  placeholder="Paste your content here to get a comprehensive score..."
                  value={scoreContent}
                  onChange={(e) => setScoreContent(e.target.value)}
                  className="min-h-[200px] mb-4"
                  data-testid="content-score-input"
                />
                
                <Button
                  onClick={handleContentScore}
                  disabled={loadingScore}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 mb-6"
                  data-testid="score-content-btn"
                >
                  {loadingScore ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Scoring...</> : <><BarChart3 className="w-4 h-4 mr-2" /> Score Content</>}
                </Button>

                {contentScoreResult && (
                  <div className="space-y-4">
                    <div className="text-center p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl">
                      <div className={`text-5xl font-bold ${contentScoreResult.overall >= 70 ? 'text-green-600' : contentScoreResult.overall >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {contentScoreResult.overall}/100
                      </div>
                      <p className="text-gray-600 mt-2">Overall Content Score</p>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">{contentScoreResult.readability}</div>
                        <p className="text-xs text-gray-600">Readability</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{contentScoreResult.structure}</div>
                        <p className="text-xs text-gray-600">Structure</p>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">{contentScoreResult.seo}</div>
                        <p className="text-xs text-gray-600">SEO</p>
                      </div>
                    </div>

                    {contentScoreResult.suggestions.length > 0 && (
                      <div className="p-4 bg-yellow-50 rounded-lg">
                        <h4 className="font-medium text-yellow-800 mb-2">Suggestions to Improve</h4>
                        <ul className="text-sm text-yellow-700 space-y-1">
                          {contentScoreResult.suggestions.map((s, i) => <li key={i}>• {s}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* Headline Analyzer */}
            {activeTool === "headline-analyzer" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                    <Lightbulb className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Headline Analyzer</h2>
                    <p className="text-gray-600">Score your headlines for maximum CTR</p>
                  </div>
                  <Badge className="ml-auto bg-gradient-to-r from-amber-500 to-orange-500 text-white">NEW</Badge>
                </div>

                <div className="flex gap-4 mb-6">
                  <Input
                    placeholder="Enter your headline..."
                    value={headlineText}
                    onChange={(e) => setHeadlineText(e.target.value)}
                    className="flex-1 text-lg"
                    data-testid="headline-input"
                  />
                  <Button
                    onClick={handleHeadlineAnalysis}
                    disabled={loadingHeadline}
                    className="bg-gradient-to-r from-amber-500 to-orange-600"
                    data-testid="analyze-headline-btn"
                  >
                    {loadingHeadline ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                  </Button>
                </div>

                {headlineResult && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center gap-6 p-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl">
                      <div className={`text-6xl font-bold ${headlineResult.score >= 70 ? 'text-green-600' : headlineResult.score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                        {headlineResult.score}
                      </div>
                      <div className={`text-4xl font-bold px-4 py-2 rounded-lg ${
                        headlineResult.grade === 'A' ? 'bg-green-100 text-green-700' :
                        headlineResult.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                        headlineResult.grade === 'C' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {headlineResult.grade}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 justify-center">
                      {headlineResult.factors.hasNumbers && <Badge className="bg-green-100 text-green-700">Numbers ✓</Badge>}
                      {headlineResult.factors.hasPowerWords && <Badge className="bg-green-100 text-green-700">Power Words ✓</Badge>}
                      {headlineResult.factors.hasQuestion && <Badge className="bg-green-100 text-green-700">Question ✓</Badge>}
                      {headlineResult.factors.hasEmotional && <Badge className="bg-green-100 text-green-700">Emotional ✓</Badge>}
                      {headlineResult.factors.lengthOptimal && <Badge className="bg-green-100 text-green-700">Length ✓</Badge>}
                      {!headlineResult.factors.hasNumbers && <Badge variant="outline">No Numbers</Badge>}
                      {!headlineResult.factors.hasPowerWords && <Badge variant="outline">No Power Words</Badge>}
                    </div>

                    {headlineResult.suggestions.length > 0 && (
                      <div className="p-4 bg-amber-50 rounded-lg">
                        <h4 className="font-medium text-amber-800 mb-2">How to Improve</h4>
                        <ul className="text-sm text-amber-700 space-y-1">
                          {headlineResult.suggestions.map((s, i) => <li key={i}>• {s}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* Content Templates */}
            {activeTool === "templates" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Content Templates</h2>
                    <p className="text-gray-600">Ready-to-use templates for various content types</p>
                  </div>
                  <Badge className="ml-auto bg-gradient-to-r from-teal-500 to-cyan-500 text-white">NEW</Badge>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {contentTemplates.map((template) => (
                    <div 
                      key={template.id}
                      className="p-4 border rounded-xl hover:shadow-lg transition-all hover:border-teal-300 cursor-pointer group"
                      onClick={() => loadTemplate(template)}
                      data-testid={`template-${template.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline" className="text-xs">{template.category}</Badge>
                        <Copy className="w-4 h-4 text-gray-400 group-hover:text-teal-500 transition-colors" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">{template.name}</h4>
                      <p className="text-xs text-gray-500 line-clamp-3 font-mono bg-gray-50 p-2 rounded">
                        {template.template.substring(0, 100)}...
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-teal-50 rounded-lg">
                  <h4 className="font-medium text-teal-800 mb-2">How to use templates</h4>
                  <ul className="text-sm text-teal-700 space-y-1">
                    <li>• Click on any template to load it into the AI Writer</li>
                    <li>• Replace placeholder text with your content</li>
                    <li>• Use AI to expand or rewrite sections</li>
                  </ul>
                </div>
              </Card>
            )}

            {/* AI Content Rewriter */}
            {activeTool === "rewriter" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <Repeat className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">AI Content Rewriter</h2>
                    <p className="text-gray-600">Paraphrase text while maintaining meaning using GPT-4o</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Content to Rewrite</label>
                    <Textarea
                      placeholder="Paste the content you want to rewrite (minimum 50 characters)..."
                      rows={8}
                      className="resize-none"
                      value={rewriteText}
                      onChange={(e) => setRewriteText(e.target.value)}
                      data-testid="rewrite-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">{rewriteText.length} characters</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Writing Style</label>
                      <Select value={rewriteStyle} onValueChange={setRewriteStyle}>
                        <SelectTrigger data-testid="rewrite-style-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional - Business appropriate</SelectItem>
                          <SelectItem value="casual">Casual - Friendly & conversational</SelectItem>
                          <SelectItem value="formal">Formal - Academic & serious</SelectItem>
                          <SelectItem value="creative">Creative - Engaging & vivid</SelectItem>
                          <SelectItem value="academic">Academic - Scholarly language</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="preserveLength"
                        checked={preserveLength}
                        onChange={(e) => setPreserveLength(e.target.checked)}
                        className="w-4 h-4 text-purple-600 rounded"
                        data-testid="preserve-length-checkbox"
                      />
                      <label htmlFor="preserveLength" className="text-sm">
                        Preserve original length
                      </label>
                    </div>
                  </div>

                  <Button 
                    onClick={handleContentRewrite}
                    disabled={loadingRewrite}
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    data-testid="rewrite-content-btn"
                  >
                    {loadingRewrite ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Rewriting Content...</>
                    ) : (
                      <><Repeat className="w-4 h-4 mr-2" /> Rewrite Content</>
                    )}
                  </Button>

                  {/* Rewritten Result */}
                  {rewriteResult && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                          <span className="font-medium">Content Rewritten Successfully</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>Original: {rewriteResult.original_word_count} words</span>
                          <span>Rewritten: {rewriteResult.rewritten_word_count} words</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Original */}
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Original</span>
                            <Badge variant="outline">{rewriteResult.original_word_count} words</Badge>
                          </div>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap max-h-64 overflow-y-auto">
                            {rewriteText}
                          </p>
                        </div>

                        {/* Rewritten */}
                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-purple-700">Rewritten ({rewriteStyle})</span>
                            <div className="flex gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => copyToClipboard(rewriteResult.rewritten_content)}
                                data-testid="copy-rewritten-btn"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => downloadAsTextFile(rewriteResult.rewritten_content, 'rewritten_content')}
                                data-testid="download-rewritten-btn"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                            {rewriteResult.rewritten_content}
                          </p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-3">
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setRewriteText(rewriteResult.rewritten_content);
                            setRewriteResult(null);
                          }}
                          data-testid="rewrite-again-btn"
                        >
                          <Repeat className="w-4 h-4 mr-2" />
                          Rewrite Again
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setPlagiarismText(rewriteResult.rewritten_content);
                            setActiveTool("plagiarism");
                          }}
                          data-testid="check-plagiarism-rewritten-btn"
                        >
                          <Search className="w-4 h-4 mr-2" />
                          Check for Plagiarism
                        </Button>
                      </div>
                    </div>
                  )}

                  {!rewriteResult && (
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <h4 className="font-medium text-purple-800 mb-2">How it works</h4>
                      <ul className="text-sm text-purple-700 space-y-1">
                        <li>• Paste your content above (minimum 50 characters)</li>
                        <li>• Choose a writing style that fits your needs</li>
                        <li>• AI will completely rewrite while preserving meaning</li>
                        <li>• Check the rewritten content for plagiarism to verify uniqueness</li>
                      </ul>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* My Drafts */}
            {activeTool === "my-drafts" && (
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <FolderOpen className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">My Drafts</h2>
                      <p className="text-gray-600">View and manage your saved content</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchDrafts}
                    disabled={loadingDrafts}
                    data-testid="refresh-drafts-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingDrafts ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>

                {loadingDrafts ? (
                  <div className="py-12 text-center">
                    <RefreshCw className="w-8 h-8 mx-auto animate-spin text-purple-600 mb-2" />
                    <p className="text-gray-500">Loading drafts...</p>
                  </div>
                ) : drafts.length === 0 ? (
                  <div className="py-12 text-center">
                    <FolderOpen className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 mb-2">No Drafts Yet</h3>
                    <p className="text-gray-500 mb-4">Generate content with AI Writer and save it as a draft</p>
                    <Button onClick={() => setActiveTool("ai-writer")} className="bg-purple-600 hover:bg-purple-700">
                      <Wand2 className="w-4 h-4 mr-2" /> Go to AI Writer
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {drafts.map((draft) => (
                      <div 
                        key={draft.draft_id} 
                        className="p-4 border rounded-lg hover:shadow-md transition-shadow bg-white"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 truncate">{draft.title}</h4>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <Badge variant="outline" className="text-xs capitalize">{draft.content_type?.replace('_', ' ')}</Badge>
                              <Badge variant="outline" className="text-xs">{draft.word_count} words</Badge>
                              {draft.tone && (
                                <Badge variant="outline" className="text-xs capitalize">{draft.tone}</Badge>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-2">
                              Saved: {new Date(draft.created_at).toLocaleDateString('en-IN', { 
                                year: 'numeric',
                                month: 'short', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => loadDraft(draft)}
                              data-testid={`load-draft-${draft.draft_id}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Load
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => deleteDraft(draft.draft_id)}
                              data-testid={`delete-draft-${draft.draft_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        {draft.keywords?.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <div className="flex flex-wrap gap-1">
                              {draft.keywords.slice(0, 5).map((kw, idx) => (
                                <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                  {kw}
                                </span>
                              ))}
                              {draft.keywords.length > 5 && (
                                <span className="text-xs text-gray-400">+{draft.keywords.length - 5} more</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {/* Fallback for any other tools */}
            {!["ai-writer", "my-drafts", "topic-research", "content-analyzer", "readability", "calendar", "plagiarism", "content-score", "headline-analyzer", "templates", "rewriter"].includes(activeTool) && (
              <Card className="p-6 text-center">
                <div className="py-12">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {contentTools.find(t => t.id === activeTool)?.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {contentTools.find(t => t.id === activeTool)?.name}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {contentTools.find(t => t.id === activeTool)?.description}
                  </p>
                  <Badge>Coming Soon</Badge>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Related Tools Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <RelatedTools currentToolId={activeTool} category="content" />
        </div>
      </div>
      <ToolSEOContent toolId="content-tools" />
      <Footer />
      <WhatsAppChat />
    </div>
  );
}
